import { FormProvider, useForm } from '@slal/ui/react-hook-form/core';
import { Heading } from '@slal/ui/typography';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ButtonGroup, CardButton, ProgressStep, useAnalytics } from '~/components';
import { useFormData } from '~/hooks';
import { InvestmentAmountAndChargesCard, InvestmentDetailsCard, InvestmentFundsCard } from '../application';
import { strings } from '../strings';
import { StyledForm } from '../styles';

const defaultValues = {
    clientName: '',
    productName: '',
    drawdown: undefined,
    drawdownPercent: undefined,
    drawdownAmount: '',
    drawdownType: 'amount',
    contributionsType: 'amount',
    contributionsAmount: '',
    annualManagementCharge_type: 'percent',
    initialAdviserCharge_type: 'percent',
    ongoingAdviserCharge_type: 'percent',
    initialAdviserCharge_amount: '',
    initialAdviserCharge_percent: '',
    ongoingAdviserCharge_amount: '',
    ongoingAdviserCharge_percent: '',
};

export const ReportDetails = () => {
    const initialized = useRef(false);
    const navigate = useNavigate();
    const { formData, updateFormData } = useFormData();
    const hookForm = useForm({
        defaultValues: { ...defaultValues, ...formData },
        mode: 'onChange',
    });
    const { pushEvent, setHasFiredAnalytics, resetAnalytics } = useAnalytics();
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            pushEvent({ eventAction: 'Pageload', eventLabel: 'Page 1' });
        }
    }, []);

    const onValidSubmit = (values: Record<string, unknown>) => {
        updateFormData(values);
        navigate('/reporting-period');
    };

    const [initialChargeAmount, initialAdviserChargePercent, ongoingChargeAmount, ongoingAdviserChargePercent] =
        hookForm.watch([
            'initialAdviserCharge_amount',
            'initialAdviserCharge_percent',
            'ongoingAdviserCharge_amount',
            'ongoingAdviserCharge_percent',
        ]);
    const isDrawdownPercentError = hookForm.formState.errors?.drawdownPercent?.message;
    const isInitialChargeValid = !!initialChargeAmount || !!initialAdviserChargePercent;
    const isOngoingChargeValid = !!ongoingChargeAmount || !!ongoingAdviserChargePercent;

    useEffect(() => {
        const subscription = hookForm.watch(() => {
            resetAnalytics();
        });
        return () => subscription.unsubscribe();
    }, [hookForm.watch, resetAnalytics]);

    const [isTotalAllocationError, setIsTotalAllocationError] = useState(false);

    const isContinueButtonVisible = [
        isInitialChargeValid,
        isOngoingChargeValid,
        !isTotalAllocationError,
        !isDrawdownPercentError,
    ].every(Boolean);

    return (
        <FormProvider {...hookForm}>
            <StyledForm onSubmit={hookForm.handleSubmit(onValidSubmit)} id="reportDetailsForm">
                <ProgressStep currentStep={1} totalSteps={3} />
                <Heading level={2}>{strings.reportDetails}</Heading>
                <InvestmentDetailsCard />
                <InvestmentFundsCard setIsTotalAllocationError={setIsTotalAllocationError} />
                <InvestmentAmountAndChargesCard />
                <ButtonGroup>
                    {isContinueButtonVisible && (
                        <CardButton
                            onClick={() => {
                                pushEvent({
                                    eventAction: 'Click',
                                    eventLabel: `Continue`,
                                });
                                setHasFiredAnalytics(true);
                            }}
                            type="submit"
                        >
                            Continue
                        </CardButton>
                    )}
                </ButtonGroup>
            </StyledForm>
        </FormProvider>
    );
};
